import jStat from 'jstat';
import has from 'lodash/has';
import keys from 'lodash/keys';
import { round6, round7 } from 'utilities/math';
import {
  sum,
  mean,
  sampleStandardDeviation
} from 'simple-statistics';

export const getAccumulator = (arr, key) =>
  arr.reduce((accumulator, currentValue, i) =>
    accumulator + +currentValue[`${key}${i + 1}`]
  , 0);

export const makeGrandMean = (groups, countObservation) =>
  round6(groups.reduce((accumulator, currentValue, i) =>
    accumulator + (currentValue[`sampleMeanGroup${i + 1}`] * currentValue[`sampleSizeGroup${i + 1}`])
  , 0) / countObservation);

export const makeTreatmentSumOfSquares = (groups, countObservation) => {
  const grandMean = makeGrandMean(groups, countObservation);

  return round7(groups.reduce((accumulator, currentValue, i) => {
    const sqrMeansDiff = (currentValue[`sampleMeanGroup${i + 1}`] - grandMean) ** 2;
    return accumulator + (currentValue[`sampleSizeGroup${i + 1}`] * sqrMeansDiff);
  }
  , 0));
};

export const makeTreatmentSumOfSquaresWithDataSetScenario = (data, countObservation, sumOfAllObservations) => {
  const levels = keys(data);
  return round7(round6(levels.reduce((accumulator, item) => {
    const T = sum(data[item]);
    return accumulator + ((T ** 2) / data[item].length);
  }, 0)) - ((sumOfAllObservations ** 2) / countObservation));
};

export const makeErrorSumOfSquares = groups =>
  round7(groups.reduce((accumulator, currentValue, i) =>
    accumulator + ((currentValue[`sampleSizeGroup${i + 1}`] - 1) * (currentValue[`stDeviationGroup${i + 1}`] ** 2))
  , 0));

export const makeErrorSumOfSquaresWithDataSetScenario = data => {
  const levels = keys(data);
  return round7(levels.reduce((accumulator, item) => {
    const squaredData = data[item].map(i => i ** 2);
    const sumOfSquares = sum(squaredData);
    const T = sum(data[item]);
    const countObservation = data[item].length;
    return accumulator + (sumOfSquares - (T ** 2) / countObservation);
  }, 0));
};

const makePValue = (fscore, df1, df2) =>
  round7(jStat.ftest(fscore, df1, df2));

const makeQ = (confInt, k, N) =>
  jStat.tukey.inv(+confInt / 100, k, N - k);

export const calculateTableStatisticsAnova = (groups, anovaConfidenceInterval = 95, meanSquareForError) => {
  const confidenceLevel = (100 - +anovaConfidenceInterval) / 100;
  const n = groups.reduce((acc, group, i) => acc + Number(group[`sampleSizeGroup${i + 1}`]), 0);
  const dof = n - groups.length;

  return groups.map((group, i) => {
    const sampleMean = Number(group[`sampleMeanGroup${i + 1}`]);
    const standardDeviation = Number(group[`stDeviationGroup${i + 1}`]);
    const sampleSize = Number(group[`sampleSizeGroup${i + 1}`]);
    const tCritical = jStat.studentt.inv(confidenceLevel / 2, dof);
    const change = Math.abs(tCritical * Math.sqrt(meanSquareForError / sampleSize));
    const confInterval = [sampleMean - change, sampleMean + change];
    return ([
      group.name,
      sampleSize,
      round6(sampleMean),
      standardDeviation,
      `( ${round6(confInterval[0])} , ${round6(confInterval[1])} )`
    ]);
  }
  );
};

export const calculateTableForAnalysisAnova = groups => {
  const countGroup = groups.length;
  const countObservation = getAccumulator(groups, 'sampleSizeGroup');

  const treatmentSumOfSquares = makeTreatmentSumOfSquares(groups, countObservation);

  const errorSumOfSquares = makeErrorSumOfSquares(groups);

  const ssTotal = round7(treatmentSumOfSquares + errorSumOfSquares);

  const meanSquareForTreatments = round7(treatmentSumOfSquares / (countGroup - 1));
  const meanSquareForError = round7(errorSumOfSquares / (countObservation - countGroup));
  const FStat = round7(meanSquareForTreatments / meanSquareForError);
  const pValue = makePValue(FStat, countGroup - 1, countObservation - countGroup);

  return (
    {
      tableForAnalysis: [
        ['Treatments', countGroup - 1, treatmentSumOfSquares, meanSquareForTreatments, FStat, pValue],
        ['Error', countObservation - countGroup, errorSumOfSquares, meanSquareForError, '', ''],
        ['Total', countObservation - 1, ssTotal, '', '', '']
      ],
      meanSquareForError
    }
  );
};

export const changeViewDataForAnova = (categoricalValues, numericalValues) => {
  const sumOfAllObservations = sum(numericalValues);
  const dataWithNewView = categoricalValues.reduce((accumulator, item, i) => {
    if (has(accumulator, item)) {
      accumulator[item].push(numericalValues[i]);
    } else {
      accumulator[item] = [numericalValues[i]];
    }
    return accumulator;
  }, {});

  return ({
    dataWithNewView,
    sumOfAllObservations
  });
};

export const makeMeanSquareForError = (data, countObservation, errorSum) => {
  const countGroup = keys(data).length;
  const errorSumOfSquares = errorSum ? errorSum : makeErrorSumOfSquaresWithDataSetScenario(data);

  return round7(errorSumOfSquares / (countObservation - countGroup));
};

export const calculateTableForAnalysisAnovaWithDataSetScenario = (data, countObservation, sumOfAllObservations) => {
  const countGroup = keys(data).length;
  const treatmentSumOfSquares = makeTreatmentSumOfSquaresWithDataSetScenario(data, countObservation, sumOfAllObservations);
  const errorSumOfSquares = makeErrorSumOfSquaresWithDataSetScenario(data);

  const ssTotal = round7(treatmentSumOfSquares + errorSumOfSquares);
  const meanSquareForTreatments = round7(treatmentSumOfSquares / (countGroup - 1));
  const meanSquareForError = makeMeanSquareForError(data, countObservation, errorSumOfSquares);
  const FStat = round7(meanSquareForTreatments / meanSquareForError);
  const pValue = makePValue(FStat, countGroup - 1, countObservation - countGroup);

  return ({
    tableForAnalysis: [
      ['Treatments', countGroup - 1, treatmentSumOfSquares, meanSquareForTreatments, FStat, pValue],
      ['Error', countObservation - countGroup, errorSumOfSquares, meanSquareForError, '', ''],
      ['Total', countObservation - 1, ssTotal, '', '', '']
    ],
    meanSquareForError
  });
};

export const calculateTableStatisticsAnovaWithDataSetScenario = (data, n, anovaConfidenceInterval = 95, meanSquareForError) => {
  const confidenceLevel = (100 - +anovaConfidenceInterval) / 100;
  const levels = keys(data);
  const confInt = (1 - confidenceLevel) + confidenceLevel / 2;
  const dof = n - levels.length;

  return levels.map(level => {
    const sampleSize = data[level].length;
    // eslint-disable-next-line init-declarations
    let confInterval, sampleMean, standardDeviation;

    if (sampleSize === 1) {
      [sampleMean] = data[level];
      standardDeviation = 'NA';
      const tScore = jStat.studentt.inv(confInt, dof);
      const standardError = makeErrorSumOfSquaresWithDataSetScenario(data);
      const meanSquareForErr = round6(standardError / dof);
      const diff = tScore * Math.sqrt(meanSquareForErr / sampleSize);

      confInterval = [sampleMean - diff, sampleMean + diff];
    } else {
      sampleMean = mean(data[level]);
      standardDeviation = round6(sampleStandardDeviation(data[level]));
      const tCritical = jStat.studentt.inv(confidenceLevel / 2, dof);
      const change = Math.abs(tCritical * Math.sqrt(meanSquareForError / sampleSize));
      confInterval = [sampleMean - change, sampleMean + change];
    }

    return ([
      level,
      sampleSize,
      round6(sampleMean),
      standardDeviation,
      `( ${round6(confInterval[0])} , ${round6(confInterval[1])} )`
    ]);
  });
};

const getLoverBoundAndUpperBound = (error, q, s1, s2, diffMeans) => {
  const helpValue = round6(q * Math.sqrt((error / 2) * ((1 / s1) + (1 / s2))));
  const loverBound = round6(diffMeans - helpValue);
  const upperBound = round6(diffMeans + helpValue);
  return `(${loverBound} , ${upperBound})`;
};

export const calculateTukeyKramerPostHocAnalysis = (data, confInt, N, meanSquareForError) => {
  const levels = keys(data);
  const n = levels.length;
  const q = makeQ(confInt, n, N);

  const arr = [];
  for (let i = 0; i < n; i++) {
    const level = levels[i];

    const sampleMeanCurrent = round6(mean(data[level]));
    const sizeCurrent = data[level].length;

    for (let j = i + 1; j < n; j++) {
      const nextLevel = levels[j];
      const sampleMeanNext = round6(mean(data[nextLevel]));
      const sizeNext = data[nextLevel].length;
      const bounds = getLoverBoundAndUpperBound(meanSquareForError, q, sizeCurrent, sizeNext, sampleMeanCurrent - sampleMeanNext);

      arr.push([`${level} - ${nextLevel}`, bounds]);
    }
  }

  return ({
    tableForTukeyKramerPostHocAnalysis: arr,
    qForStudentizedRangeDistribution: q
  });
};

export const recalculateTableForTukeyKramerPostHocAnalysis =
  (categoricalVariable, numericalVariable, anovaConfidenceInterval) => {
    const { dataWithNewView } = changeViewDataForAnova(categoricalVariable, numericalVariable);
    const meanSquareForError = makeMeanSquareForError(dataWithNewView, numericalVariable.length);

    return ({
      ...calculateTukeyKramerPostHocAnalysis(dataWithNewView, anovaConfidenceInterval, numericalVariable.length, meanSquareForError)
    });
  };

export const calculateTukeyKramerAnalysisNoDataset = (groups, anovaConfidenceInterval) => {
  const summaryStatisticByFactor = groups.map((groupRow, i) => ({ [groupRow.name]: groupRow[`sampleMeanGroup${i + 1}`] }));
  const N = groups.reduce((acc, groupRow, i) => acc + Number(groupRow[`sampleSizeGroup${i + 1}`]), 0);
  const n = groups.length;

  const countGroup = groups.length;
  const countObservation = getAccumulator(groups, 'sampleSizeGroup');

  const errorSumOfSquares = makeErrorSumOfSquares(groups);
  const meanSquareForError = round6(errorSumOfSquares / (countObservation - countGroup));

  const q = makeQ(anovaConfidenceInterval, n, N);

  const resultingArr = [];

  groups.forEach((group, i) => {
    const sampleMeanCurrent = group[`sampleMeanGroup${i + 1}`];
    const sizeCurrent = group[`sampleSizeGroup${i + 1}`];

    for (let j = i + 1; j < n; j++) {
      const nextLevel = groups[j];
      const sampleMeanNext = nextLevel[`sampleMeanGroup${j + 1}`];
      const sizeNext = nextLevel[`sampleSizeGroup${j + 1}`];

      const bounds = getLoverBoundAndUpperBound(meanSquareForError, q, sizeCurrent, sizeNext, sampleMeanCurrent - sampleMeanNext);

      resultingArr.push([`${group.name} - ${nextLevel.name}`, bounds]);
    }
  });

  return {
    summaryStatisticByFactor,
    tableForTukeyKramerPostHocAnalysis: resultingArr,
    qForStudentizedRangeDistribution: q
  };
};

export const calculateDataForAnovaWithDataSetScenario =
  (categoricalVariable, numericalVariable, anovaConfidenceInterval) => {
    const { dataWithNewView, sumOfAllObservations } = changeViewDataForAnova(categoricalVariable, numericalVariable);
    const { meanSquareForError, tableForAnalysis } = calculateTableForAnalysisAnovaWithDataSetScenario(dataWithNewView, numericalVariable.length, sumOfAllObservations);

    return ({
      tableData: tableForAnalysis,
      tableDataStat: calculateTableStatisticsAnovaWithDataSetScenario(dataWithNewView, numericalVariable.length, anovaConfidenceInterval, meanSquareForError),
      ...calculateTukeyKramerPostHocAnalysis(dataWithNewView, anovaConfidenceInterval, numericalVariable.length, meanSquareForError)
    });
  };
